import * as React from "react"
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group"
import { type VariantProps } from "class-variance-authority"

import { cn } from "@/utils/cn"
import { toggleVariants } from "@/components/ui/toggle"
import { FieldError } from "./field-error"
import { Label } from "./label"
import { Col } from "../grid/col"
import { useFormikContext } from "formik"

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  size: "default",
  variant: "default",
})

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn("flex items-center justify-start gap-1", className)}
    {...props}
  >
    <ToggleGroupContext.Provider value={{ variant, size }}>
      {children}
    </ToggleGroupContext.Provider>
  </ToggleGroupPrimitive.Root>
))

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({ className, children, variant, size, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext)

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  )
})

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

type FormikToogleProps = {
  size?: number;
  label?: string;
  field?: any;
  name?: string;
  itens?: any[];
  getOptionLabel?(value: any): string;
  getOptionValue?(value: any): string;
  children?: any[];
  onChange?(value: any): void;
}

export const FormikToogle = (props: FormikToogleProps) => {
  const formikProps = useFormikContext<any>();
  const {values, setFieldValue, setFieldTouched, handleChange, handleBlur} = formikProps;
  const name = props.field?.name || props.name;

  return (
    <>
    <Col size={props.size}>
      {props.label && (
        <Label>{props.label}</Label>
      )}
      <ToggleGroup className='w-full' type="single" variant={'outline'} value={values[name]} onValueChange={(value) => {
        setFieldValue(name, value);
        props.onChange && props.onChange(value);
      }}>
        {props.children ? props.children : (
            <>
            {props.itens?.map(item => (
              <ToggleGroupItem size={"default"} value={props.getOptionValue!(item)}>{props.getOptionLabel!(item)}</ToggleGroupItem>
            ))}
            </>
        )}
      </ToggleGroup>
      <FieldError name={name} />
    </Col>
    </>
  )
}

export { ToggleGroup, ToggleGroupItem }
